import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';

@DwComponent({
  key: 'document-pdf-viewer',
  name: 'Document PDF Viewer',
  componentType: DwComponentType.pageComponent,
  isGlobal: true,
  //parentItemName: [ AppMetaDataItemNames.ProcessorConfig ]
})
@Component({
  selector: 'app-document-embedded-viewer',
  template: `
    <div class="embedded-detail" [attr.aria-busy]="isLoading">
      <app-document-detail-skeleton *ngIf="isLoading" [showHeader]="false" [interactive]="true"></app-document-detail-skeleton>
      <div class="embedded-detail__content" [class.embedded-detail__pending]="isLoading"
        [attr.aria-hidden]="isLoading ? true : null">
        <dw-html-view [ngModel]="safeEmbeddedHtml"></dw-html-view>
      </div>
    </div>
  `,
  styleUrls: ['./document-embedded-viewer.component.scss']
})
export class DocumentEmbeddedViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() embeddedHtml: string = '<div>This is a test</div>';

  safeEmbeddedHtml: string = this.embeddedHtml;
  isLoading = false;

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly zone: NgZone) { }

  ngOnChanges(): void {
    const html = this.disableSketchfabAutospin(this.embeddedHtml);
    if (html !== this.safeEmbeddedHtml) {
      this.safeEmbeddedHtml = html;
      this.isLoading = /<iframe\b/i.test(html || '');
    }
  }

  ngAfterViewInit(): void {
    // Capture non-bubbling load events from the iframe injected by dw-html-view.
    this.host.nativeElement.addEventListener('load', this.onFrameLoad, true);
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('load', this.onFrameLoad, true);
  }

  private readonly onFrameLoad = (event: Event): void => {
    const frame = event.target as HTMLIFrameElement;
    if (frame?.tagName === 'IFRAME' && this.host.nativeElement.contains(frame)) {
      this.zone.run(() => this.isLoading = false);
    }
  };

  // Sketchfab embeds spin the model when the iframe URL has `autospin=N`
  // with N > 0. We force it to 0 on the way in so existing CMS records
  // don't all have to be edited by hand.
  private disableSketchfabAutospin(html: string): string {
    if (!html) {
      return html;
    }

    let result = html.replace(/([?&])autospin=[^&"'\s>]*/gi, '$1autospin=0');

    // If a Sketchfab embed URL doesn't already specify autospin, append it
    // so the default platform behavior can't reintroduce a spin.
    result = result.replace(
      /(src=["'][^"']*sketchfab\.com\/models\/[^"']+?\/embed(?:\?[^"']*)?)(["'])/gi,
      (match, srcUpToQuote: string, quote: string) => {
        if (/[?&]autospin=/i.test(srcUpToQuote)) {
          return match;
        }
        const joiner = srcUpToQuote.includes('?') ? '&' : '?';
        return `${srcUpToQuote}${joiner}autospin=0${quote}`;
      }
    );

    return result;
  }
}
